// src/helpers/get_process_user_data.js
import {
    white_list_incoming_person_fields,
    allowed_all_hmac_key_names,
    personFieldBasicTypes,
    white_list_outgoing_person_fields,
    at_least_one_required_form_identifier_fields,
    empty_array_keys_to_skip,
    listArrayKeys
} from './static_vars';
import { hmac_param_name_to_plain_field_name } from './action_request_helpers';

import { get_user_data_from_cors_iframe } from '../forms/cors_iframe';

import {
    extract_array_values_from_url_parameter,
    detect_parse_url_param_types,
} from './parse_url_params';

import {
    string_array_of_ints_to_array,
    fetch_with_timeout,
    remove_empty_values_from_object
} from './index';

import {
    validate_single_field_for_handlebars,
    validate_and_clean_handlebars_from_user_data
} from './handlebars_validation';

import defaultsDeep from 'lodash.defaultsdeep';
import { build_data_hub_action_privacy_headers } from './data_hub_privacy_policy';
import {
	decode_unverified_jwt_data_payload,
	read_organization_jwt_cache_entry,
	should_skip_network_fetch_for_cached_organization_jwt,
	write_organization_jwt_cache_entry,
} from './organization_jwt_local_storage_cache';
import { merge_user_data_browser } from './user_data_smart_merge';

const has_own_property = (object_to_check, key_name) => Object.prototype.hasOwnProperty.call(object_to_check || {}, key_name);

const is_explicit_empty_array_url_value = (raw_url_value = null) => {
	if (Array.isArray(raw_url_value)) {
		return raw_url_value.length === 0;
	}

	if (typeof raw_url_value !== 'string') {
		return false;
	}

	const trimmed_url_value = raw_url_value.trim();
	return trimmed_url_value === '' || trimmed_url_value === '[]';
};

/**
 * Reads the URL parameters and returns a plain object
 * representing user data from the URL.
 * URL values here override any previously stored values.
 * param {URLSearchParams} url_params - The URL parameters to process.
 * param {Object} data_hub_instance - The data hub instance to use.
 * @returns {Object} - An object containing user data from the URL.
 */
export const getURLUserData = async (url_params, data_hub_instance=window?.data_hub) => {
    const result = {};
    const processed_array_keys = new Set();
    if (!(url_params instanceof URLSearchParams)) {
        return result;
    }
    url_params.forEach((value, key) => {
        // TCPA / consent UI: never ingest sms_optin from URL query params — pre-checked SMS
        // opt-in must reflect server-validated profile (JWT / HMAC GET, CORS cache, constructor
        // user_data), not shareable links.
        if (key === 'sms_optin') {
            return;
        }
        if (white_list_incoming_person_fields.includes(key)) {
            try {
                // Use the key's known type to determine if should treat as array
                // (not the value format — a single value like "140326" is still an array field)
                if (listArrayKeys.includes(key)) {
                    if (processed_array_keys.has(key)) {
                        return;
                    }
                    processed_array_keys.add(key);

                    const raw_array_values = url_params.getAll(key);
                    const should_preserve_explicit_empty_array = empty_array_keys_to_skip.includes(key)
                        && raw_array_values.length > 0
                        && raw_array_values.every(raw_value => is_explicit_empty_array_url_value(raw_value));
                    const arrayValues = should_preserve_explicit_empty_array
                        ? []
                        : raw_array_values.reduce((combined_array_values, raw_array_value) => {
                            if (is_explicit_empty_array_url_value(raw_array_value)) {
                                return combined_array_values;
                            }

                            return combined_array_values.concat(
                                extract_array_values_from_url_parameter(raw_array_value)
                            );
                        }, []);
                    let parsed = [];
                    // Check static_vars.personFieldBasicTypes[key] for type
                    // If = 'int[]' then parse as array of ints
                    if (personFieldBasicTypes[key] && personFieldBasicTypes[key] === 'int[]') {
                        // If should be ints
                        parsed = string_array_of_ints_to_array(arrayValues);
                    } else if (personFieldBasicTypes[key] && personFieldBasicTypes[key] === 'string[]') {
                        // If should be strings
                        parsed = arrayValues.map(item => item.trim());
                    } else {
                        data_hub_instance?.error_function?.('Unrecognized array type for key: ' + key, null);
                        parsed = arrayValues;
                    }
                    
                    // For arrays, check each element for handlebars
                    try {
                        const validated_array = parsed.filter(item => {
                            const validated_item = validate_single_field_for_handlebars(
                                key, 
                                item, 
                                white_list_incoming_person_fields, 
                                data_hub_instance?.error_function
                            );
                            return validated_item !== null;
                        });
                        
                        if (validated_array.length > 0) {
                            result[key] = validated_array;
                        } else if (should_preserve_explicit_empty_array) {
                            result[key] = [];
                        }
                    } catch (error) {
                        data_hub_instance?.error_function?.(`Error validating array field '${key}' for handlebars: ${error}`, null);
                        if (parsed.length > 0 || should_preserve_explicit_empty_array) {
                            // If validation fails, use the original parsed array or keep an explicit empty-array clear.
                            result[key] = parsed;
                        }
                    }
                } else {
                    const parsed_value = detect_parse_url_param_types(value);
                    // If no parse, dont validate filter handlebars
                    if (
                        parsed_value === null ||
                        parsed_value === undefined ||
                        (typeof parsed_value === 'string' && parsed_value === '')
                    ) {
                        return;
                    }
                    // Validate for unparsed handlebars before assigning
                    try {
                        const validated_value = validate_single_field_for_handlebars(
                            key, 
                            parsed_value, 
                            white_list_incoming_person_fields, 
                            data_hub_instance?.error_function
                        );
                        if (validated_value !== null) {
                            result[key] = validated_value;
                        }
                    } catch (error) {
                        data_hub_instance?.error_function?.(`Error validating field '${key}' for handlebars: ${error}`, null);
                        // If validation fails, use the original parsed value
                        result[key] = parsed_value;
                    }
                }
            } catch (error) {
                console.warn('Error parsing URL param:', key, value, error);
            }
        } else if (allowed_all_hmac_key_names.includes(key) &&
            typeof value === 'string' &&
            value.length > 10) {
            result[key] = value;
        }
    });
    return result;
};

/**
 * Copies only keys in white_list_outgoing_person_fields from a server person payload
 * (GET user_jwt, clear_jwt returns.user_data, CORS iframe cache, etc.) into browser user_data.
 * That list is “safe to show in the client,” not “safe for the client to submit” — use
 * white_list_incoming_person_fields for URL params and request bodies.
 *
 * @param {Object|null|undefined} server_user_object
 * @param {Object} [data_hub_instance]
 * @returns {Object}
 */
export function whitelist_server_person_object_using_outgoing_field_list(
    server_user_object,
    data_hub_instance = window?.data_hub
) {
    let returned_user_data_whitelist = {};
    if (
        server_user_object &&
        typeof server_user_object === 'object' &&
        !Array.isArray(server_user_object)
    ) {
        Object.keys(server_user_object).forEach((key) => {
            if (white_list_outgoing_person_fields.includes(key)) {
                returned_user_data_whitelist[key] = server_user_object[key];
            }
        });
    }

    try {
        const cleaned_user_data = validate_and_clean_handlebars_from_user_data(
            returned_user_data_whitelist,
            white_list_outgoing_person_fields,
            data_hub_instance?.error_function
        );
        return cleaned_user_data || {};
    } catch (error) {
        data_hub_instance?.error_function?.(
            `Error validating server user object for handlebars: ${error}`,
            null
        );
        return returned_user_data_whitelist || {};
    }
}

/**
 * Fetches user data from JWT.
 * Returns a plain object (or {} if nothing was found or on error).
 */
export const getJWTUserData = async (data_hub_instance = window?.data_hub) => {
    if (!data_hub_instance) {
        throw new Error('Data hub reference is not defined.');
    }

    try {
        let these_user_hmac_key_values = {};
        let has_at_least_one_hmac_hash = false;

        // Ensure url_params is a URLSearchParams instance
        if (!data_hub_instance.url_params || !(data_hub_instance.url_params instanceof URLSearchParams)) {
            data_hub_instance.url_params = new URLSearchParams(window.location.search);
        }

        // Process HMAC keys if needed.
        // Sources for the hash (h_iterable_*): URL params first, then config.user_data
        // Sources for the identifier (email, cell_phone, etc.): URL params first, then config.user_data
        // URL params always take priority over config.user_data
        if (data_hub_instance.get_iterable_user_data_via_hmac_key) {
            const plain_field_names_already_bound = new Set();
            for (const hmac_key_name of allowed_all_hmac_key_names) {
                const plain_field_name = hmac_param_name_to_plain_field_name(hmac_key_name);

                const hmac_hash = data_hub_instance.url_params.has(hmac_key_name)
                    ? data_hub_instance.url_params.get(hmac_key_name)
                    : (data_hub_instance.user_data && data_hub_instance.user_data[hmac_key_name]) || null;

                if (!hmac_hash) continue;

                const identifier = data_hub_instance.url_params.has(plain_field_name)
                    ? data_hub_instance.url_params.get(plain_field_name)
                    : (data_hub_instance.user_data && data_hub_instance.user_data[plain_field_name]) || null;

                if (!identifier) continue;

                if (plain_field_names_already_bound.has(plain_field_name)) {
                    continue;
                }
                plain_field_names_already_bound.add(plain_field_name);

                these_user_hmac_key_values[hmac_key_name] = hmac_hash;
                these_user_hmac_key_values[plain_field_name] = identifier;
                has_at_least_one_hmac_hash = true;
            }
        }

        if (!data_hub_instance.forms_domain) {
            data_hub_instance?.error_function?.('No forms domain provided for user JWT fetch.', null);
            return {};
        }

        let user_jwt_url = `${data_hub_instance.forms_domain}/api/get/user_jwt`;
        const user_jwt_query_params = new URLSearchParams();
        if (has_at_least_one_hmac_hash && Object.keys(these_user_hmac_key_values).length > 0) {
            Object.entries(these_user_hmac_key_values).forEach(([param_key, param_value]) => {
                user_jwt_query_params.append(param_key, param_value);
            });
        }
        if (data_hub_instance.organization_id) {
            user_jwt_query_params.set('organization_id', data_hub_instance.organization_id.trim());
        }
        let paramsString = user_jwt_query_params.toString();
        if (paramsString) {
            user_jwt_url += `?${paramsString}`;
        }

        if (has_at_least_one_hmac_hash) {
            let new_url = new URL(window.location.href);
            for (const hmac_param_key of allowed_all_hmac_key_names) {
                new_url.searchParams.delete(hmac_param_key);
            }
            if (history && history.replaceState) {
                history.replaceState(null, '', new_url.toString());
            }
        }

        // If forms_domain is cross-origin, hand off to CORS iframe and return empty object.
        if (data_hub_instance.forms_domain !== window.location.origin) {
            get_user_data_from_cors_iframe(data_hub_instance.forms_domain, paramsString, data_hub_instance);
            return {};
        }

        const response = await fetch_with_timeout(user_jwt_url, {
            credentials: 'include',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...build_data_hub_action_privacy_headers(data_hub_instance),
            },
            timeout: 10000
        });
        if (!response.ok) {
            // Surface a clear developer error when the backend can't resolve the organization.
            // This typically means a shared domain (Cloud Run) is being used without an explicit organization_id.
            if (response.status === 403 && !data_hub_instance.organization_id) {
                data_hub_instance?.error_function?.(
                    'User JWT fetch returned 403 — no organization_id was provided and the backend could not resolve one from the request origin. '
                    + 'Add data-hub-organization-id to the <data-hub-signup-form> element or pass organization_id in the DataHub constructor config.',
                    null
                );
            } else {
                data_hub_instance?.error_function?.('User JWT fetch failed: ' + response.status + ' ' + response.statusText, null);
            }
            return {};
        }

        const json = await response.json();

        if (json && typeof json === 'object') {

            // 250311 New object returns { user_data: returned_user_data, jwt_cookie: jwt_cookie }
            // but want backwards compatible. i dont think need to change since user_data is still under .user... ?
            // if there is a jwt_cookie, set on the data_hub_instance
            if (json.jwt_cookie) {
                data_hub_instance.jwt_cookie = json.jwt_cookie;
                write_organization_jwt_cache_entry(data_hub_instance, json.jwt_cookie);
            }

            const json_user_object = json.user || json.user_data || json || {};
            return whitelist_server_person_object_using_outgoing_field_list(
                json_user_object,
                data_hub_instance
            );
        } else {
            data_hub_instance?.error_function?.('User JWT response is not an object: ' + typeof json, null);
            return {};
        }
    } catch (error) {
        if (error.name !== 'AbortError') {
            data_hub_instance?.error_function?.('Fetch with timeout ended request early: ' + error, null);
        } else {
            data_hub_instance?.error_function?.('User JWT fetch error: ' + error, null);
        }
        return {};
    }
};

export const load_user_data_from_jwt_url_params = async (data_hub_instance = window?.data_hub) => {
    if (!data_hub_instance) {
        throw new Error('Data hub reference is not defined.');
    }

    // Optionally preserve any existing user_data if you want to merge onto it:
    const existing_data = data_hub_instance.user_data || {};

    // 1. JWT-shaped user data: skip GET /api/get/user_jwt when org-scoped localStorage cache is fresh.
    let jwtData = {};
    if (should_skip_network_fetch_for_cached_organization_jwt(data_hub_instance)) {
        const cached = read_organization_jwt_cache_entry(data_hub_instance.organization_id);
        if (cached?.jwt) {
            data_hub_instance.jwt_cookie = cached.jwt;
            const raw_payload_user_object = decode_unverified_jwt_data_payload(cached.jwt);
            jwtData = whitelist_server_person_object_using_outgoing_field_list(
                raw_payload_user_object,
                data_hub_instance
            );
        }
    } else {
        jwtData = await getJWTUserData(data_hub_instance);
    }

    // 2. Get URL params data.
    const urlData = await getURLUserData(data_hub_instance.url_params, data_hub_instance);

    // 3. Merge data so that URL values override JWT (and any existing data).
    //    JWT / constructor user_data may contain complexKeys arrays — use merge_user_data_browser
    //    (jwtData wins over existing_data) instead of defaultsDeep index-merging arrays.
    //    URL scalars and listArrayKeys are still layered below without index-merge.
    const mergedDataWithJWT = await merge_user_data_browser(existing_data, jwtData);
    const urlScalarData = { ...urlData };
    const mergedUserScalarData = { ...mergedDataWithJWT };

    // Pull out array fields before defaultsDeep mangles them
    const urlArrayFields = {};
    const mergedUserArrayFields = {};
    Object.keys(urlScalarData).forEach(key => {
        if (Array.isArray(urlScalarData[key])) {
            urlArrayFields[key] = urlScalarData[key];
            delete urlScalarData[key];
        }
    });
    Object.keys(mergedUserScalarData).forEach(key => {
        if (Array.isArray(mergedUserScalarData[key])) {
            mergedUserArrayFields[key] = mergedUserScalarData[key];
            delete mergedUserScalarData[key];
        }
    });

    // Merge scalar fields normally
    const mergedDataWithURL = defaultsDeep({}, urlScalarData, mergedUserScalarData);

    // Preserve array values without index-merging: URL arrays override JWT/existing arrays,
    // including explicit empty arrays used to clear subscription/list fields.
    const allArrayKeys = new Set([...Object.keys(mergedUserArrayFields), ...Object.keys(urlArrayFields)]);
    allArrayKeys.forEach(key => {
        if (has_own_property(urlArrayFields, key)) {
            mergedDataWithURL[key] = urlArrayFields[key];
            return;
        }

        if (has_own_property(mergedUserArrayFields, key)) {
            mergedDataWithURL[key] = mergedUserArrayFields[key];
        }
    });

    data_hub_instance.user_data = mergedDataWithURL;
    window.data_hub_user_data = mergedDataWithURL;

    // 4. Fire off IP info lookup (fire and forget).
    // 260317 TODO disable for now since going to put on WAY more traffic. Update the new action data hub port to grab from headers instead
    // get_user_ip_info_assign_to_metadata(data_hub_instance, data_hub_instance?.error_function);

    return mergedDataWithURL;
};

/**
 * Fetches the user's IP info.
 */
export const get_user_ip_info = async (error_function = window?.data_hub?.error_function) => {
    try {
        const response = await fetch_with_timeout("https://ipapi.co/json");
        if (!response.ok) {
            return {};
        }
        return await response.json();
    } catch (error) {
        return {}; // Return empty object instead of undefined
    }
};

/**
 * Assigns the user's IP info to data_hub_instance.page_metadata.
 */
export const get_user_ip_info_assign_to_metadata = async (data_hub_instance = window?.data_hub, error_function = window?.data_hub?.error_function) => {
    try {
        const data = await get_user_ip_info(error_function) || {};
        data_hub_instance.page_metadata = data_hub_instance.page_metadata || {};
        data_hub_instance.page_metadata.ip_data = data;

        // If has country_code assign to user_data
        if (!data_hub_instance.user_data) { data_hub_instance.user_data = {}; }
        if (!window.data_hub_user_data) { window.data_hub_user_data = {}; }
        if (data.country_code && (!data_hub_instance?.user_data?.country_code)) {
            data_hub_instance.user_data.country_code = data.country_code;
            window.data_hub_user_data.country_code =data.country_code;
        }
        if (data.country_name && (!data_hub_instance?.user_data?.country)) {
            data_hub_instance.user_data.country = data.country_name;
            window.data_hub_user_data.country = data.country_name;
        }

    } catch (error) {
        // Ensure we have an object even if the fetch fails
        if (data_hub_instance && data_hub_instance.page_metadata) {
            data_hub_instance.page_metadata.ip_data = data_hub_instance.page_metadata.ip_data || {};
        }
    }
};

/**
 * Filter inputs to look funder < 18 age and return a boolean.
 * @param {Object} user_data
 * @returns {boolean}
 */
export const is_under_18 = (user_data, data_hub = window?.data_hub) => {
    if (!user_data) {
        return false;
    }

    const age_keys = ['birth_date', 'birth_year'];
    // must have at least one age key otherwise return
    if (!age_keys.some(key => user_data[key])) {
        return false;
    }

    // if has birth_date, try to parse it
    try {
        if (user_data.birth_date) {
            const birth_date = new Date(user_data.birth_date);
            if (birth_date instanceof Date && !isNaN(birth_date)) {
                const today = new Date();
                let age = today.getFullYear() - birth_date.getFullYear();
                // Adjust if birthday hasn't occurred yet this year
                const month_diff = today.getMonth() - birth_date.getMonth();
                if (month_diff < 0 || (month_diff === 0 && today.getDate() < birth_date.getDate())) {
                    age--;
                }
                return age < 18;
            }
        }
    } catch (error) {
        console.warn("Error parsing birth_date:", error);
    }

    if (!user_data?.birth_year) return false;
    // Coerce birth_year explicitly so non-numeric strings ("abc") become NaN-aware
    // and we don't fail open. (Number("1990") is fine; Number("abc") is NaN.)
    try {
        const parsed_birth_year = parseInt(user_data.birth_year, 10);
        if (!Number.isFinite(parsed_birth_year)) return false;
        const age = new Date().getFullYear() - parsed_birth_year;
        return age < 18;
    } catch (error) {
        if(data_hub) data_hub?.error_function?.('Error parsing birth_year', error);
        return false;
    }
};

/**
 * Normalizes user data fields to their expected types.
 * @param {Object} userData
 * @returns {Object} normalizedData
 */
export const normalizeUserData = (userData) => {
    const normalizedData = {};

    Object.keys(userData).forEach((key) => {
        if (personFieldBasicTypes[key]) {
            switch (personFieldBasicTypes[key]) {
                case "string":
                    normalizedData[key] = String(userData[key]);
                    break;
                case "int":
                    normalizedData[key] = parseInt(userData[key], 10) || null;
                    break;
                case "float":
                    normalizedData[key] = parseFloat(userData[key]) || null;
                    break;
                case "boolean":
                    normalizedData[key] = userData[key] === "true" || userData[key] === true;
                    break;
                case "int[]":
                    normalizedData[key] = Array.isArray(userData[key])
                        ? userData[key].map(i => parseInt(i, 10) || null).filter(i => i !== null)
                        : [];
                    break;
                default:
                    normalizedData[key] = userData[key]; // Fallback, keep the original
            }
        } else {
            normalizedData[key] = userData[key]; // Keep any extra fields untouched
        }
    });

    return normalizedData;
}

/**
 * Removes any user data keys that are not whitelisted.
 * @param {Object} data_hub_action_object_to_send_to_api
 */
export function removeNonWhitelistedUserDataKeys(user_data) {
    if (!user_data || typeof user_data !== 'object') {
        return {};
    }
    const keysToRemove = Object.keys(user_data).filter(key =>
        !white_list_outgoing_person_fields.includes(key) &&
        !allowed_all_hmac_key_names.includes(key)
    );

    keysToRemove.forEach(key => {
        delete user_data[key];
    });

    return user_data;
}

/**
 * Cleans user data by normalizing it, removing non-whitelisted keys, and empty values.
 * @param {Object} this_new_user_data 
 * @param {Array} array of key names to skip when removing empty values 
 * @returns {Object} cleanedUserData
 */
export const cleanUserData = (this_new_user_data, keys_to_skip = empty_array_keys_to_skip) => {
    if (!this_new_user_data) {
        return {};
    }
    this_new_user_data = normalizeUserData(this_new_user_data);

    // Remove any non-whitelisted keys from the user data
    this_new_user_data = removeNonWhitelistedUserDataKeys(this_new_user_data);

    // Remove empty values and arrays from the user data
    this_new_user_data = remove_empty_values_from_object(this_new_user_data, 0, 5, keys_to_skip);

    // Ensure only one unique key name for each key
    // TODO do we want to do this? idk. i guess it would be duplicate message type IDs?
    // or maybe like custom field topIssues = ['guns','environment','guns']
    this_new_user_data = removeDuplicateValuesFromArrays(this_new_user_data);

    return this_new_user_data;
}

/**
 * Loops through an object to find arrays n deep. Removes duplicate values from arrays.
 * @param {Object} obj
 * @param {number} depth
 * @param {number} max_depth
 * @returns {Object} obj
 */
export const removeDuplicateValuesFromArrays = (obj, depth = 0, max_depth = 5) => {
    try {
        if (depth > max_depth) {
            return obj;
        }

        Object.keys(obj).forEach(key => {
            if (Array.isArray(obj[key])) {
                const arr = obj[key];
                // Skip processing if array is empty or has only one element.
                if (arr.length < 2) {
                    return;
                }

                const seen = new Set();
                const duplicates = new Set();
                const uniqueArr = [];

                // Single pass to collect unique items and record duplicates.
                arr.forEach(item => {
                    if (seen.has(item)) {
                        duplicates.add(item);
                    } else {
                        seen.add(item);
                        uniqueArr.push(item);
                    }
                });

                // Log the actual duplicate values if any were found.
                if (duplicates.size > 0) {
                    console.warn(`Removed duplicate values from array '${key}':`, Array.from(duplicates));
                }
                obj[key] = uniqueArr;
            } else if (obj[key] !== null && typeof obj[key] === 'object') {
                obj[key] = removeDuplicateValuesFromArrays(obj[key], depth + 1, max_depth);
            }
        });

        return obj;
    } catch (e) {
        console.warn('Error removing duplicate values from arrays:', e);
        return obj;
    }
};